import type { TenantDatabaseEntry } from '@/userManagement/domain/entities/User';

function normalizeAlias(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/** Parses a wire row (string or object) into a display entry. Returns null when no canonical name is found. */
export function parseTenantDatabaseEntry(entry: unknown): TenantDatabaseEntry | null {
  if (typeof entry === 'string') {
    const name = entry.trim();
    return name ? { tenantDatabaseName: name, databaseAlias: null } : null;
  }
  if (entry === null || typeof entry !== 'object') return null;

  const o = entry as Record<string, unknown>;
  const nameRaw = o.tenantDatabaseName ?? o.name ?? o.databaseName ?? o.dbName ?? o.groupName ?? o.GroupName;
  const tenantDatabaseName = typeof nameRaw === 'string' ? nameRaw.trim() : '';
  if (!tenantDatabaseName) return null;

  const databaseAlias = normalizeAlias(o.databaseAlias ?? o.dbAlias ?? o.alias ?? o.description);
  const databaseIdRaw = o.databaseId ?? o.DatabaseId;
  const databaseId = typeof databaseIdRaw === 'string' && databaseIdRaw.trim() !== '' ? databaseIdRaw.trim() : undefined;

  return {
    tenantDatabaseName,
    databaseAlias,
    ...(databaseId ? { databaseId } : {})
  };
}

/** Dedupes by canonical `tenantDatabaseName` (case-insensitive), preserving first-seen alias. */
export function dedupeTenantDatabaseEntries(entries: TenantDatabaseEntry[]): TenantDatabaseEntry[] {
  const seen = new Set<string>();
  const out: TenantDatabaseEntry[] = [];
  for (const entry of entries) {
    const key = entry.tenantDatabaseName.trim().toLowerCase();
    if (key === '' || seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}
